/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { PCSettings } from '../../../core/entities';
import { InteractiveComponents, PCState } from '../../../core/enums';
import { ParentalControlServiceImpl } from '../../../core/impls';
import { ServicesClientImpl } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import { Log } from '../../../core/logging';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import type { TGetState, TivoPayloadAction } from '../../../core/tivo/tivo-redux-wrapper';
import { BrandingUiUtility } from '../../../core/utils';
import { PlayerViewMode } from '../../../core/videoplayer/enums';
import { VideoPlaybackController } from '../../../core/videoplayer/VideoPlaybackController';
import {
    clearActiveInteractiveComponent,
    setActiveInteractiveComponent,
} from '../../interactivelayer/interactiveLayer.reducers';
import {
    hideOverlayDialog,
    overlayDialogSetCaretInvisible,
    overlayDialogSetCaretVisible,
    showOverlayDialog,
} from '../../overlaydialog/overlaydialog.reducers';
import { IOverlayDialog } from '../../overlaydialog/overlaydialogprops';
import {
    setScreenTaskController,
    showHideAdultContentScreen,
    showSetRatingLimitsScreen,
} from '../../screens/screens.reducers';
import {
    setPCSettingsFocusedPositionAction,
    setPCStateAction,
} from '../../settings/parentalcontrols/parentalcontrols.actions';
import { showWhisper } from '../../whisper/whisper.reducer';
import { setOverlayHighlightHorizontalPositionAction } from '../overlayhighlight/overlayhighlight.actions';
import {
    overlayDisableGhostMode,
    overlayEnableGhostMode,
} from '../overlayhighlight/overlayhighlight.reducers';
import { OverlayTypes } from '../OverlayTypes';
import {
    resetPCPinValuesAction,
    setChangePCPinOverlayActiveAction,
    setFirstPcPinAction,
    setPcPinAction,
    setPCPinOnSuccessCallbackAction,
    setPcPinSpinnerActiveAction,
    setPcPinSpinnerDelayAction,
} from './pcpinoverlay.actions';
import {
    RESET_PC_PIN_VALUES,
    SET_FIRST_PIN_FOR_CHANGING_PIN,
    SET_IS_CHANGE_PIN_OVERLAY_ACTIVE,
    SET_PC_PIN,
    SET_PC_PIN_OVERLAY_UI_PROPS,
    SET_PC_PIN_SPINNER_ACTIVE,
    SET_PC_PIN_SPINNER_DELAY,
    SET_PC_PIN_SUCCESS_CALLBACK,
} from './pcpinoverlay.types';

interface IPcPin {
    firstPCPin: string;
    isChangePCPinOvelayActive: boolean;
    pcPin: string;
    spinnerActive: boolean;
    spinnerDelay: number;
    onSuccessCallback: Function;
    overlayUiProps: Partial<IOverlayDialog>;
}

const initialState: IPcPin = {
    firstPCPin: '',
    isChangePCPinOvelayActive: false,
    pcPin: '',
    spinnerActive: false,
    spinnerDelay: 0,
    onSuccessCallback: () => false,
    overlayUiProps: {},
};

const TAG = 'pcPinOverlay';

const pcPinOverlayReducer = (
    state = initialState,
    { type, payload } = <TivoPayloadAction<IPcPin>>{},
) => {
    switch (type) {
        case SET_PC_PIN:
            return {
                ...state,
                pcPin: payload.pcPin,
            };
        case SET_PC_PIN_SPINNER_ACTIVE:
            return {
                ...state,
                spinnerActive: payload.spinnerActive,
            };
        case SET_PC_PIN_SPINNER_DELAY:
            return {
                ...state,
                spinnerDelay: payload.spinnerDelay,
            };
        case SET_PC_PIN_SUCCESS_CALLBACK:
            return {
                ...state,
                onSuccessCallback: payload.onSuccessCallback,
            };
        case SET_FIRST_PIN_FOR_CHANGING_PIN:
            return {
                ...state,
                firstPCPin: payload.firstPCPin,
            };
        case SET_IS_CHANGE_PIN_OVERLAY_ACTIVE:
            return {
                ...state,
                isChangePCPinOvelayActive: payload.isChangePCPinOvelayActive,
            };
        case SET_PC_PIN_OVERLAY_UI_PROPS:
            return {
                ...state,
                overlayUiProps: payload.overlayUiProps,
            };
        case RESET_PC_PIN_VALUES:
            return {
                ...state,
                firstPCPin: payload.firstPCPin,
                isChangePCPinOvelayActive: payload.isChangePCPinOvelayActive,
                pcPin: payload.pcPin,
            };
        default:
            return state;
    }
};

export const GLOBAL_MOUSE_TRAP: string = 'HidePcPinOverlay';

/**
 * Updates a specific character in the given PIN string at the specified index with a new input.
 */
const getUpdatedPcPin = (pin: string, index: number, pinInput: string): string => {
    const pcPinArray = pin.split('');
    pcPinArray[index] = pinInput;
    return pcPinArray.join('');
};

export const hideChangePinOverlay = () => (dispatch: Function) => {
    dispatch(setPcPinAction(''));
    dispatch(hideOverlayDialog());
};

export const hidePcPinOverlay = () => (dispatch: Function) => {
    dispatch(resetPCPinValuesAction());
    dispatch(hideOverlayDialog());
};

export const onKeyBackPcPinOverlay = () => (dispatch: Function) => {
    dispatch(hidePcPinOverlay());
};

export const onKeyDigitPcPinOverlay = () => (dispatch: Function, getState: Function) => {
    const { key } = getState().inputFactory;
    const { pcPin } = getState().pcPinOverlay;
    const { horizontalPosition } = getState().overlayHighlight;
    dispatch(setPcPinAction(getUpdatedPcPin(pcPin, horizontalPosition[0], key)));
    dispatch(onKeyRightPcPinOverlay());
};

export const onKeyDownPcPinOverlay = () => (dispatch: Function, getState: Function) => {
    const { pcPin } = getState().pcPinOverlay;
    const { horizontalPosition } = getState().overlayHighlight;

    const updatedPinInput: number = ((Number(pcPin[horizontalPosition[0]]) || 0) + 9) % 10;
    dispatch(
        setPcPinAction(getUpdatedPcPin(pcPin, horizontalPosition[0], String(updatedPinInput))),
    );
};

export const onKeyLeftPcPinOverlay = () => (dispatch: Function, getState: Function) => {
    const { horizontalPosition } = getState().overlayHighlight;

    if (horizontalPosition[0] > 0) {
        dispatch(setOverlayHighlightHorizontalPositionAction([horizontalPosition[0] - 1]));
    }
};

export const onKeyOkPcPinOverlay = () => (dispatch: Function) => {
    dispatch(onKeyRightPcPinOverlay());
};

export const onKeyRightPcPinOverlay = () => (dispatch: Function, getState: Function) => {
    const { horizontalPosition, horizontalLimit } = getState().overlayHighlight;
    const { pcPin } = getState().pcPinOverlay;

    if (!pcPin[horizontalPosition[0]]) {
        dispatch(setPcPinAction(getUpdatedPcPin(pcPin, horizontalPosition[0], '0')));
    }

    if (horizontalPosition[0] >= horizontalLimit[0] - 1) {
        Log.i(TAG, 'pc pin has been committed.');
        dispatch(handlePcPinSubmit());
    } else {
        dispatch(setOverlayHighlightHorizontalPositionAction([Number(horizontalPosition[0]) + 1]));
    }
};

const handlePcPinSubmit = () => (dispatch: Function, getState: Function) => {
    const { spinnerDelay, pcPin, isChangePCPinOvelayActive, onSuccessCallback } =
        getState().pcPinOverlay;
    const services = new ServicesClientImpl();

    if (isChangePCPinOvelayActive) {
        dispatch(handleChangePin());
        return;
    }

    const isPinCorrect = services.verifyPCPin(pcPin);

    // Increasing spinner delay by 1 second for every incorrect attempt.
    dispatch(setPcPinSpinnerDelayAction(isPinCorrect ? 0 : spinnerDelay + 1000));
    dispatch(setPcPinSpinnerActiveAction(!isPinCorrect));
    dispatch(hidePcPinOverlay());

    if (!isPinCorrect) {
        dispatch(showIncorrectPinOverlay());
        return;
    }

    services.updatePCSetting(PCSettings.VERIFIED_PIN_TIMESTAMP, new Date().getTime().toString());
    try {
        dispatch(onSuccessCallback());
    } catch (error) {
        Log.e(TAG, 'handlePcPinSubmit', error);
    }
    dispatch(setPCPinOnSuccessCallbackAction(() => false));
};

const handleChangePin = () => (dispatch: Function, getState: Function) => {
    const { pcPin, firstPCPin } = getState().pcPinOverlay;
    const services = new ServicesClientImpl();

    if (firstPCPin.length === 0) {
        // Pin first entry.
        dispatch(hideChangePinOverlay());
        dispatch(setFirstPcPinAction(pcPin));
        dispatch(showPCConfirmPinOverlay(services.getPCState()));
    } else if (firstPCPin === pcPin) {
        // Pin second entry - Both Pin matched.
        dispatch(handlePinMatched());
    } else {
        // Pin second entry - Both Pin not matched.
        dispatch(hideChangePinOverlay());
        dispatch(showPinNotMatchedOverlay());
    }
};

const handlePinMatched = () => (dispatch: Function, getState: Function) => {
    const { pcPin } = getState().pcPinOverlay;
    const services = new ServicesClientImpl();

    services.updatePCSetting(PCSettings.DEFAULT_PC_PIN, pcPin);
    dispatch(hidePcPinOverlay());

    if (services.getPCState() === PCState.Off) {
        const { verticalPosition, menuOptions } = getState().parentalControls;
        const newPCState = PCState.Unlocked;

        services.updatePCState(newPCState);
        dispatch(updatePcState(newPCState));

        switch (menuOptions[verticalPosition]?.title) {
            case Locale.getString(LocaleKeys.SETTINGS_PC_HIDE_ADULT_COTENT_TEXT):
                dispatch(showHideAdultContentScreen());
                break;
            case Locale.getString(LocaleKeys.SETTINGS_PC_SET_RATING_LIMITS_TEXT):
                dispatch(showSetRatingLimitsScreen());
                break;
            default:
        }
    } else {
        dispatch(showWhisper());
    }
};

export const handlePCScreenVerifiedPin = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition, menuOptions } = getState().parentalControls;
    const services = new ServicesClientImpl();
    const pcState = services.getPCState();

    switch (menuOptions[verticalPosition]?.title) {
        case Locale.getString(LocaleKeys.SETTINGS_PC_PARENTAL_CONTROLS_TEXT): {
            const newPCState = pcState === PCState.Locked ? PCState.Unlocked : PCState.Locked;
            dispatch(updatePcState(newPCState));
            break;
        }

        case Locale.getString(LocaleKeys.SETTINGS_PC_HIDE_ADULT_COTENT_TEXT):
            dispatch(updatePcState(PCState.Unlocked));
            dispatch(showHideAdultContentScreen());
            break;

        case Locale.getString(LocaleKeys.SETTINGS_PC_SET_RATING_LIMITS_TEXT):
            dispatch(updatePcState(PCState.Unlocked));
            dispatch(showSetRatingLimitsScreen());
            break;

        case Locale.getString(LocaleKeys.SETTINGS_PC_DISABLE_PARENTAL_CONTROLS_TEXT):
            dispatch(resetPCPinValuesAction());
            dispatch(setPCSettingsFocusedPositionAction(0, true));
            dispatch(updatePcState(PCState.Off));
            break;

        case Locale.getString(LocaleKeys.SETTINGS_PC_CHANGE_PIN_TEXT):
            dispatch(showPCNewPinOverlay());
            break;

        default:
    }
};

export const updatePcState = (state: PCState) => (dispatch: Function) => {
    const services = new ServicesClientImpl();

    dispatch(setPCStateAction(state));
    services.updatePCState(state);
};

export const onKeyUpPcPinOverlay = () => (dispatch: Function, getState: Function) => {
    const { pcPin } = getState().pcPinOverlay;
    const { horizontalPosition } = getState().overlayHighlight;

    const updatedPinInput: number = ((Number(pcPin[horizontalPosition[0]]) || 0) + 1) % 10;
    dispatch(
        setPcPinAction(getUpdatedPcPin(pcPin, horizontalPosition[0], String(updatedPinInput))),
    );
};

export const onMouseClickPcPinOverlay = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;
    const { horizontalPosition } = getState().overlayHighlight;

    switch (value) {
        case InteractiveComponents.NORTH:
            dispatch(onKeyUpPcPinOverlay());
            break;
        case InteractiveComponents.SOUTH:
            dispatch(onKeyDownPcPinOverlay());
            break;
        case GLOBAL_MOUSE_TRAP:
            dispatch(hidePcPinOverlay());
            break;
        default:
            if (horizontalPosition[0] === 3) dispatch(onKeyRightPcPinOverlay());
            break;
    }
};

export const onMouseOutPcPinOverlay = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;
    if (value === InteractiveComponents.NORTH || value === InteractiveComponents.SOUTH) {
        dispatch(clearActiveInteractiveComponent());
    } else {
        dispatch(overlayEnableGhostMode());
    }
};

export const onMouseOverPcPinOverlay = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;
    const { horizontalPosition } = getState().overlayHighlight;

    if (Number(value) === horizontalPosition[0] + 1) dispatch(onKeyRightPcPinOverlay());
    else if (Number(value) === horizontalPosition[0] - 1) dispatch(onKeyLeftPcPinOverlay());

    dispatch(onMouseOverPcPinOperations());
};

export const onMouseOverPcPinOperations = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;
    const { pcPin } = getState().pcPinOverlay;
    const { horizontalPosition } = getState().overlayHighlight;

    if (!pcPin[horizontalPosition[0]]) {
        dispatch(setPcPinAction(getUpdatedPcPin(pcPin, horizontalPosition[0], '0')));
    }

    if (value !== GLOBAL_MOUSE_TRAP && Number(value) === horizontalPosition[0]) {
        dispatch(overlayDisableGhostMode());
    }

    if (value === GLOBAL_MOUSE_TRAP) dispatch(overlayDialogSetCaretInvisible());
    else {
        dispatch(overlayDialogSetCaretVisible());
    }

    if (value === InteractiveComponents.NORTH || value === InteractiveComponents.SOUTH) {
        dispatch(setActiveInteractiveComponent(value));
    }
};

export const showIncorrectPinOverlay = () => (dispatch: Function, getState: Function) => {
    const { spinnerActive } = getState().pcPinOverlay;
    let incorrectPinDescription = Locale.getString(LocaleKeys.PC_INCORRECT_PIN_OVERLAY_DESCRIPTION);
    if (spinnerActive) {
        incorrectPinDescription = '';
    }
    dispatch(
        showPcPinOverlay(
            Locale.getString(LocaleKeys.PC_INCORRECT_PIN_OVERLAY_TITLE),
            incorrectPinDescription,
        ),
    );
};

export const showEnterPCPinOverlay = (onSuccessCallback: Function) => (dispatch: Function) => {
    dispatch(setPCPinOnSuccessCallbackAction(onSuccessCallback));
    dispatch(
        showPcPinOverlay(
            Locale.getString(LocaleKeys.PC_ENTER_PIN_OVERLAY_TITLE),
            Locale.getString(LocaleKeys.PC_ENTER_PIN_ON_LOCKED_OVERLAY_DESCRIPTION),
        ),
    );
};

export const showPCPinOverlayForPCSettingsAccess =
    (onSuccessCallback: Function) => (dispatch: Function) => {
        dispatch(setPCPinOnSuccessCallbackAction(onSuccessCallback));
        const descriptionLocaleKey = ParentalControlServiceImpl.isPinRecentlyVerified()
            ? LocaleKeys.PC_ENTER_PIN_OVERLAY_DESCRIPTION
            : LocaleKeys.PC_ENTER_PIN_ON_LOCKED_OVERLAY_DESCRIPTION;
        dispatch(
            showPcPinOverlay(
                Locale.getString(LocaleKeys.PC_ENTER_PIN_OVERLAY_TITLE),
                Locale.getString(descriptionLocaleKey),
            ),
        );
    };

export const showPCPinOverlayWithProgramRating =
    (onSuccessCallback: Function, isAdult: boolean, ratingName?: String) =>
    (dispatch: Function) => {
        let description = '';

        // Player has slightly different description if compared with the Guide.
        const isFullScreenPlayer =
            VideoPlaybackController.getInstance().getPlayerViewMode() === PlayerViewMode.FullScreen;

        dispatch(setPCPinOnSuccessCallbackAction(onSuccessCallback));

        if (isAdult) {
            const localeKey = isFullScreenPlayer
                ? LocaleKeys.PC_ENTER_PIN_RATING_ADULT_OVERLAY_DESCRIPTION
                : LocaleKeys.PC_ENTER_PIN_RATING_ADULT_OVERLAY_GUIDE_DESCRIPTION;
            description = Locale.getString(localeKey);
        } else if (ratingName) {
            const localeKey = isFullScreenPlayer
                ? LocaleKeys.PC_ENTER_PIN_RAING_OVERLAY_DESCRIPTION
                : LocaleKeys.PC_ENTER_PIN_RATING_OVERLAY_GUIDE_DESCRIPTION;
            description = BrandingUiUtility.formatDescription(Locale.getString(localeKey), {
                RATING: `$regular-text-highlight: ${ratingName}$`,
            });
        } else {
            const localeKey = isFullScreenPlayer
                ? LocaleKeys.PC_ENTER_PIN_UNKNOWN_RATING_OVERLAY_DESCRIPTION
                : LocaleKeys.PC_ENTER_PIN_UNKNOWN_RATING_OVERLAY_GUIDE_DESCRIPTION;
            description = Locale.getString(localeKey);
        }

        dispatch(
            showPcPinOverlay(Locale.getString(LocaleKeys.PC_ENTER_PIN_OVERLAY_TITLE), description),
        );
    };

export const showDisablePCPinOverlay = (onSuccessCallback: Function) => (dispatch: Function) => {
    dispatch(setPCPinOnSuccessCallbackAction(onSuccessCallback));
    dispatch(
        showPcPinOverlay(
            Locale.getString(LocaleKeys.SETTINGS_PC_DISABLE_PARENTAL_CONTROLS_TEXT),
            Locale.getString(LocaleKeys.PC_DISABLE_PARENTAL_CONTROLS_OVERLAY_DESCRIPTION),
        ),
    );
};

export const showPCCurrentPinOverlay = (onSuccessCallback: Function) => (dispatch: Function) => {
    dispatch(setPCPinOnSuccessCallbackAction(onSuccessCallback));
    dispatch(
        showPcPinOverlay(
            Locale.getString(LocaleKeys.PC_CURRENT_PIN_OVERLAY_TITLE),
            Locale.getString(LocaleKeys.PC_CURRENT_PIN_OVERLAY_DESCRIPTION),
        ),
    );
};

const showPCConfirmPinOverlay = (pcState: PCState) => (dispatch: Function) => {
    const description =
        pcState === PCState.Off
            ? Locale.getString(LocaleKeys.PC_CONFIRM_PIN_OVERLAY_DESCRIPTION)
            : Locale.getString(LocaleKeys.PC_CONFIRM_NEW_PIN_OVERLAY_DESCRIPTION);
    dispatch(
        showPcPinOverlay(Locale.getString(LocaleKeys.PC_CONFIRM_PIN_OVERLAY_TITLE), description),
    );
};

const showPCNewPinOverlay = () => (dispatch: Function) => {
    dispatch(setFirstPcPinAction(''));
    dispatch(setChangePCPinOverlayActiveAction(true));
    dispatch(
        showPcPinOverlay(
            Locale.getString(LocaleKeys.PC_NEW_PIN_OVERLAY_TITLE),
            Locale.getString(LocaleKeys.PC_NEW_PIN_OVERLAY_DESCRIPTION),
        ),
    );
};

const showPinNotMatchedOverlay = () => (dispatch: Function) => {
    dispatch(setFirstPcPinAction(''));
    dispatch(
        showPcPinOverlay(
            Locale.getString(LocaleKeys.PC_NEW_PIN_OVERLAY_TITLE),
            Locale.getString(LocaleKeys.PC_NEW_PIN_NOT_MATCHED_DESCRIPTION),
        ),
    );
};

export const showCreatePinOvelay = () => (dispatch: Function) => {
    dispatch(setFirstPcPinAction(''));
    dispatch(setChangePCPinOverlayActiveAction(true));
    dispatch(
        showPcPinOverlay(
            Locale.getString(LocaleKeys.PC_CREATE_PIN_OVERLAY_TITLE),
            Locale.getString(LocaleKeys.PC_CREATE_PIN_OVERLAY_DESCRIPTION),
        ),
    );
};

export const showPcPinOverlay =
    (title: string, description: string) => (dispatch: Function, getState: TGetState) => {
        const { overlayUiProps } = getState().pcPinOverlay;

        dispatch(setScreenTaskController(ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK));
        dispatch(setOverlayHighlightHorizontalPositionAction([0]));
        const overlayDialogProps: IOverlayDialog = {
            description,
            hasDimmer: true,
            hasSeperator: false,
            mouseTrapValue: GLOBAL_MOUSE_TRAP,
            overlayType: OverlayTypes.PC_PIN,
            title,
            options: [],
            maxInputLength: 4,
            ...overlayUiProps,
        };
        dispatch(showOverlayDialog(overlayDialogProps));
    };

export default pcPinOverlayReducer;
